import { createReducer, on } from '@ngrx/store';
import type {
  ISecapState,
  SecapResourceFlags,
  SecapResourceKey,
} from '../../../interfaces/secap/ISecapState';
import * as SecapActions from '../../actions/secap/secap.actions';

export const SECAP_FEATURE_KEY = 'secap';

const createResourceFlags = <T>(value: T): SecapResourceFlags<T> => ({
  projectStatus: value,
  versions: value,
  runtimeCatalog: value,
  templateCoverage: value,
  dataSources: value,
  templateCompilations: value,
});

export const initialSecapState: ISecapState = {
  projectUuid: null,
  selectedVersionUuid: null,
  projectStatus: null,
  versions: [],
  runtimeCatalog: null,
  templateCoverage: null,
  dataSources: [],
  dataSourceDetails: {},
  dataSourceDetailLoading: {},
  dataSourceDetailErrors: {},
  templateCompilations: [],
  selectedTemplateCompilationUuid: null,
  selectedTemplateCompilation: null,
  templateCompilationLoading: false,
  templateCompilationError: null,
  templateCompilationPreparing: false,
  templateCompilationPrepareError: null,
  templateCompilationDecisionSaving: false,
  templateCompilationDecisionError: null,
  templateCompilationFinalizing: false,
  templateCompilationFinalizeError: null,
  templateCompilationDownloadLoading: false,
  templateCompilationDownloadError: null,
  versionSaving: false,
  versionSaveError: null,
  loading: createResourceFlags(false),
  loaded: createResourceFlags(false),
  errors: createResourceFlags<string | null>(null),
};

const markLoading = (state: ISecapState, resource: SecapResourceKey): ISecapState => ({
  ...state,
  loading: { ...state.loading, [resource]: true },
  errors: { ...state.errors, [resource]: null },
});

const markLoaded = (state: ISecapState, resource: SecapResourceKey): ISecapState => ({
  ...state,
  loading: { ...state.loading, [resource]: false },
  loaded: { ...state.loaded, [resource]: true },
  errors: { ...state.errors, [resource]: null },
});

const markFailed = (
  state: ISecapState,
  resource: SecapResourceKey,
  error: string,
): ISecapState => ({
  ...state,
  loading: { ...state.loading, [resource]: false },
  loaded: { ...state.loaded, [resource]: false },
  errors: { ...state.errors, [resource]: error },
});

const isCurrentProjectResponse = (
  state: ISecapState,
  projectUuid: string | null,
): boolean => state.projectUuid === projectUuid;

export const secapReducer = createReducer(
  initialSecapState,
  on(SecapActions.setSecapProjectContext, (state, { projectUuid }) => {
    if (state.projectUuid === projectUuid) {
      return state;
    }

    return {
      ...initialSecapState,
      projectUuid,
    };
  }),
  on(SecapActions.clearSecapProjectContext, () => initialSecapState),
  on(SecapActions.selectSecapVersion, (state, { versionUuid }) => ({
    ...state,
    selectedVersionUuid:
      versionUuid === null || state.versions.some((version) => version.uuid === versionUuid)
        ? versionUuid
        : state.selectedVersionUuid,
  })),

  on(SecapActions.loadSecapProjectStatus, (state) => markLoading(state, 'projectStatus')),
  on(SecapActions.loadSecapProjectStatusSuccess, (state, { projectUuid, status }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? {
          ...markLoaded(state, 'projectStatus'),
          projectStatus: status,
        }
      : state,
  ),
  on(SecapActions.loadSecapProjectStatusFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'projectStatus', error)
      : state,
  ),

  on(SecapActions.loadSecapVersions, (state) => markLoading(state, 'versions')),
  on(SecapActions.loadSecapVersionsSuccess, (state, { projectUuid, versions }) => {
    if (!isCurrentProjectResponse(state, projectUuid)) {
      return state;
    }

    const selectedVersionUuid =
      state.selectedVersionUuid &&
      versions.some((version) => version.uuid === state.selectedVersionUuid)
        ? state.selectedVersionUuid
        : (versions[0]?.uuid ?? null);

    return {
      ...markLoaded(state, 'versions'),
      versions,
      selectedVersionUuid,
    };
  }),
  on(SecapActions.loadSecapVersionsFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'versions', error)
      : state,
  ),

  on(SecapActions.patchSecapVersion, (state) => ({
    ...state,
    versionSaving: true,
    versionSaveError: null,
  })),
  on(SecapActions.patchSecapVersionSuccess, (state, { projectUuid, version }) => {
    if (!isCurrentProjectResponse(state, projectUuid)) {
      return state;
    }

    const existingVersion = state.versions.some((item) => item.uuid === version.uuid);
    const versions = existingVersion
      ? state.versions.map((item) => (item.uuid === version.uuid ? version : item))
      : [version, ...state.versions];

    return {
      ...state,
      versions,
      selectedVersionUuid: version.uuid,
      versionSaving: false,
      versionSaveError: null,
    };
  }),
  on(SecapActions.patchSecapVersionFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? {
          ...state,
          versionSaving: false,
          versionSaveError: error,
        }
      : state,
  ),

  on(SecapActions.loadSecapRuntimeCatalog, (state) => markLoading(state, 'runtimeCatalog')),
  on(SecapActions.loadSecapRuntimeCatalogSuccess, (state, { projectUuid, catalog }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? {
          ...markLoaded(state, 'runtimeCatalog'),
          runtimeCatalog: catalog,
        }
      : state,
  ),
  on(SecapActions.loadSecapRuntimeCatalogFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'runtimeCatalog', error)
      : state,
  ),

  on(SecapActions.loadSecapTemplateCoverage, (state) =>
    markLoading(state, 'templateCoverage'),
  ),
  on(SecapActions.loadSecapTemplateCoverageSuccess, (state, { projectUuid, coverage }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? {
          ...markLoaded(state, 'templateCoverage'),
          templateCoverage: coverage,
        }
      : state,
  ),
  on(SecapActions.loadSecapTemplateCoverageFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'templateCoverage', error)
      : state,
  ),

  on(SecapActions.loadSecapDataSources, (state) => markLoading(state, 'dataSources')),
  on(SecapActions.loadSecapDataSourcesSuccess, (state, { projectUuid, dataSources }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? {
          ...markLoaded(state, 'dataSources'),
          dataSources,
        }
      : state,
  ),
  on(SecapActions.loadSecapDataSourcesFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'dataSources', error)
      : state,
  ),

  on(SecapActions.loadSecapDataSourceDetail, (state, { sourceUuid }) => ({
    ...state,
    dataSourceDetailLoading: {
      ...state.dataSourceDetailLoading,
      [sourceUuid]: true,
    },
    dataSourceDetailErrors: {
      ...state.dataSourceDetailErrors,
      [sourceUuid]: null,
    },
  })),
  on(
    SecapActions.loadSecapDataSourceDetailSuccess,
    (state, { projectUuid, sourceUuid, source, snapshots }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            dataSourceDetails: {
              ...state.dataSourceDetails,
              [sourceUuid]: { source, snapshots },
            },
            dataSourceDetailLoading: {
              ...state.dataSourceDetailLoading,
              [sourceUuid]: false,
            },
            dataSourceDetailErrors: {
              ...state.dataSourceDetailErrors,
              [sourceUuid]: null,
            },
          }
        : state,
  ),
  on(
    SecapActions.loadSecapDataSourceDetailFailure,
    (state, { projectUuid, sourceUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            dataSourceDetailLoading: {
              ...state.dataSourceDetailLoading,
              [sourceUuid]: false,
            },
            dataSourceDetailErrors: {
              ...state.dataSourceDetailErrors,
              [sourceUuid]: error,
            },
          }
        : state,
  ),

  on(SecapActions.loadSecapTemplateCompilations, (state) =>
    markLoading(state, 'templateCompilations'),
  ),
  on(
    SecapActions.loadSecapTemplateCompilationsSuccess,
    (state, { projectUuid, compilations }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...markLoaded(state, 'templateCompilations'),
            templateCompilations: compilations,
          }
        : state,
  ),
  on(SecapActions.loadSecapTemplateCompilationsFailure, (state, { projectUuid, error }) =>
    isCurrentProjectResponse(state, projectUuid)
      ? markFailed(state, 'templateCompilations', error)
      : state,
  ),

  on(SecapActions.loadSecapTemplateCompilation, (state, { compilationUuid }) => ({
    ...state,
    selectedTemplateCompilationUuid: compilationUuid,
    templateCompilationLoading: true,
    templateCompilationError: null,
    templateCompilationDecisionSaving: false,
    templateCompilationDecisionError: null,
    templateCompilationFinalizing: false,
    templateCompilationFinalizeError: null,
      templateCompilationDownloadLoading: false,
    templateCompilationDownloadError: null,
    })),
  on(
    SecapActions.loadSecapTemplateCompilationSuccess,
    (state, { projectUuid, compilation }) => {
      if (!isCurrentProjectResponse(state, projectUuid)) {
        return state;
      }

      const exists = state.templateCompilations.some((item) => item.uuid === compilation.uuid);
      return {
        ...state,
        templateCompilations: exists
          ? state.templateCompilations.map((item) =>
              item.uuid === compilation.uuid ? compilation : item,
            )
          : [compilation, ...state.templateCompilations],
        selectedTemplateCompilationUuid: compilation.uuid,
        selectedTemplateCompilation: compilation,
        templateCompilationLoading: false,
        templateCompilationError: null,
        templateCompilationDecisionSaving: false,
        templateCompilationDecisionError: null,
        templateCompilationFinalizing: false,
        templateCompilationFinalizeError: null,
              templateCompilationDownloadLoading: false,
        templateCompilationDownloadError: null,
            };
    },
  ),
  on(
    SecapActions.loadSecapTemplateCompilationFailure,
    (state, { projectUuid, compilationUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid) &&
      state.selectedTemplateCompilationUuid === compilationUuid
        ? {
            ...state,
            selectedTemplateCompilation: null,
            templateCompilationLoading: false,
            templateCompilationError: error,
          }
        : state,
  ),

  on(
    SecapActions.generateSecapTemplateCompilation,
    SecapActions.uploadSecapTemplateCompilation,
    (state) => ({
      ...state,
      templateCompilationPreparing: true,
      templateCompilationPrepareError: null,
      templateCompilationDecisionSaving: false,
      templateCompilationDecisionError: null,
      templateCompilationFinalizing: false,
      templateCompilationFinalizeError: null,
          templateCompilationDownloadLoading: false,
      templateCompilationDownloadError: null,
        }),
  ),
  on(
    SecapActions.prepareSecapTemplateCompilationSuccess,
    (state, { projectUuid, compilation }) => {
      if (!isCurrentProjectResponse(state, projectUuid)) {
        return state;
      }

      const remaining = state.templateCompilations.filter(
        (item) => item.uuid !== compilation.uuid,
      );
      return {
        ...state,
        templateCompilations: [compilation, ...remaining],
        selectedTemplateCompilationUuid: compilation.uuid,
        selectedTemplateCompilation: compilation,
        templateCompilationPreparing: false,
        templateCompilationPrepareError: null,
        templateCompilationLoading: false,
        templateCompilationError: null,
      };
    },
  ),
  on(
    SecapActions.prepareSecapTemplateCompilationFailure,
    (state, { projectUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            templateCompilationPreparing: false,
            templateCompilationPrepareError: error,
          }
        : state,
  ),

  on(SecapActions.decideSecapTemplateCompilationField, (state) => ({
    ...state,
    templateCompilationDecisionSaving: true,
    templateCompilationDecisionError: null,
  })),
  on(
    SecapActions.decideSecapTemplateCompilationFieldSuccess,
    (state, { projectUuid, compilation }) => {
      if (!isCurrentProjectResponse(state, projectUuid)) {
        return state;
      }

      const exists = state.templateCompilations.some((item) => item.uuid === compilation.uuid);
      return {
        ...state,
        templateCompilations: exists
          ? state.templateCompilations.map((item) =>
              item.uuid === compilation.uuid ? compilation : item,
            )
          : [compilation, ...state.templateCompilations],
        selectedTemplateCompilationUuid: compilation.uuid,
        selectedTemplateCompilation: compilation,
        templateCompilationDecisionSaving: false,
        templateCompilationDecisionError: null,
        templateCompilationFinalizeError: null,
      };
    },
  ),
  on(
    SecapActions.decideSecapTemplateCompilationFieldFailure,
    (state, { projectUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            templateCompilationDecisionSaving: false,
            templateCompilationDecisionError: error,
          }
        : state,
  ),

  on(SecapActions.finalizeSecapTemplateCompilation, (state) => ({
    ...state,
    templateCompilationFinalizing: true,
    templateCompilationFinalizeError: null,
    templateCompilationDownloadError: null,
  })),
  on(
    SecapActions.finalizeSecapTemplateCompilationSuccess,
    (state, { projectUuid, result }) => {
      if (!isCurrentProjectResponse(state, projectUuid)) {
        return state;
      }

      const compilation = result.compilation;
      const exists = state.templateCompilations.some((item) => item.uuid === compilation.uuid);
      return {
        ...state,
        templateCompilations: exists
          ? state.templateCompilations.map((item) =>
              item.uuid === compilation.uuid ? compilation : item,
            )
          : [compilation, ...state.templateCompilations],
        selectedTemplateCompilationUuid: compilation.uuid,
        selectedTemplateCompilation: compilation,
        templateCompilationFinalizing: false,
        templateCompilationFinalizeError: null,
            };
    },
  ),
  on(
    SecapActions.finalizeSecapTemplateCompilationFailure,
    (state, { projectUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            templateCompilationFinalizing: false,
            templateCompilationFinalizeError: error,
          }
        : state,
  ),

  on(SecapActions.downloadSecapTemplateCompilation, (state) => ({
    ...state,
    templateCompilationDownloadLoading: true,
    templateCompilationDownloadError: null,
  })),
  on(
    SecapActions.downloadSecapTemplateCompilationSuccess,
    (state, { projectUuid, download }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            templateCompilationDownloadLoading: false,
            templateCompilationDownloadError: null,
          }
        : state,
  ),
  on(
    SecapActions.downloadSecapTemplateCompilationFailure,
    (state, { projectUuid, error }) =>
      isCurrentProjectResponse(state, projectUuid)
        ? {
            ...state,
            templateCompilationDownloadLoading: false,
            templateCompilationDownloadError: error,
          }
        : state,
  ),

);
